/**
 * The IRRM2 submission envelope (PROD-2215, Emport 125): what Emport builds in the browser for one
 * IRRM client and hands to whatever sends it to Joe's IRRM2 engine (`baker-alphas/irrm2`).
 *
 * ⭐ **Emport's own envelope, not the engine's contract.** The engine's `EngineInput` wants ZERO
 * curves (`engine/contract/curve.py`) and the quarter's pack holds PAR quotes, so the envelope
 * carries `par_quotes` beside an `engine_input` whose `curves` is empty. The sender bootstraps each
 * curve through the engine's own `POST /v1/curve/bootstrap`, writes the knots into
 * `engine_input.curves`, and only then POSTs `{ engine_input, options }` to `/v1/analyze/jobs`. In
 * phase 1 the sender is a developer with `tools/emport-irrm2/submit.py`; from phase 3 it is the API,
 * which reads THIS type rather than declaring its own, so the browser and the API share one contract.
 *
 * Member names are the engine's wire names (snake_case), so an envelope is sent without renaming.
 * ⚠ The engine silently DROPS a nested member it does not know (Luke's engine_contract fact 0): a 200
 * is no proof a field was used. Every member here is one `irrm2/engine/contract` declares.
 *
 * Rates are DECIMAL FRACTIONS (`0.0625`), shocks are integer BASIS POINTS, terms are MONTHS, dates
 * are `yyyy-MM-dd` — the engine's units (`engine/contract/rates.py`, `units.py`).
 */

import { EmpyreanModelType } from './empyrean.interface';

/** The envelope's own version tag — bumped when a member changes meaning, never for an addition. */
export const IRRM2_SUBMISSION_ENVELOPE = 'emport-irrm2-submission/1';

/**
 * The engine's request cap (`IRRM2_MAX_REQUEST_BYTES`), its default. Phase 4 reads the configured
 * value from the API; until then the dialog measures against this one.
 */
export const IRRM2_DEFAULT_MAX_REQUEST_BYTES = 5_000_000;

/**
 * The model types an IRRM2 engine run is offered to, and taken from (PROD-2776, Emport 194): IRRM since
 * phase 4, and CU ALM since 28 Oct 2026 (Mike: "we need to turn on the IRRM2 engine option for CU ALM
 * clients as well"). One set, read by the Generate dialog, the payload builder and the API's bar at `begin`
 * and `resubmit`, so the three cannot disagree. A client whose model type is not known yet (null) is not
 * offered one: the payload's discount rule and scenario set are chosen by model type.
 */
export const IRRM2_MODEL_TYPES: ReadonlySet<EmpyreanModelType> = new Set<EmpyreanModelType>(['IRRM', 'CU ALM']);

/** Whether a client of this model type may be sent to the engine; null (not known yet) never is. */
export const isIrrm2ModelType = (modelType: string | null | undefined): modelType is EmpyreanModelType =>
  modelType != null && IRRM2_MODEL_TYPES.has(modelType as EmpyreanModelType);

/** `InstrumentType` — a closed enum; `deposit` or `time_deposit` is a 422 on the whole payload. */
export type Irrm2InstrumentType = 'loan' | 'cd' | 'bond' | 'borrowing' | 'nmd';

/** `RateType` — `F`/`V` only; the English words are a 422 (Luke's fact 7). */
export type Irrm2RateType = 'F' | 'V';

export type Irrm2DayCountConvention = 'act_360' | 'act_365' | 'us_30_360' | 'act_act_isda' | 'act_act_isma';

/** `custom` is deliberately absent: it needs a `custom_principal` schedule Emport has no source for. */
export type Irrm2AmortizationType = 'straight_line' | 'level_pay' | 'bullet' | 'interest_only' | 'non_amortizing';

/**
 * One `Instrument` (§7). A member Emport has no value for is OMITTED, never sent `null` — a `null`
 * on a typed member can be a 422 (Luke's `build_instruments`). `raw` is never sent: the whole source
 * row is PII and half the payload's size.
 */
export interface Irrm2Instrument {
  instrument_id: string;
  /** PII — the InstrumentID itself, as brd's reader maps it; the engine masks it to the last 4 */
  source_account: string;
  instrument_type: Irrm2InstrumentType;
  /** the core's ProductCode; `''` when the row has none (the member is required) */
  type_code: string;
  product_code?: string;
  alm_account_num?: string;
  current_balance: number;
  book_balance?: number;
  rate_type: Irrm2RateType;
  coupon_rate?: number;
  rate_index?: string;
  spread?: number;
  /** `<months>m` — a bare integer would be read as YEARS (`engine/contract/units.py`) */
  reset_frequency?: string;
  next_reset_date?: string;
  rate_cap_life?: number;
  rate_floor_life?: number;
  rate_cap_periodic?: number;
  /** required on every type but `nmd` */
  maturity_date?: string;
  origination_date?: string;
  original_balance?: number;
  /** months, at most 600 */
  original_term?: number;
  original_coupon?: number;
  day_count_convention?: Irrm2DayCountConvention;
  amortization_type?: Irrm2AmortizationType;
  payment_amount?: number;
  /** MONTHS BETWEEN payments (`1` = monthly) — the reciprocal of the BRD's payments per year */
  payment_frequency?: number;
  /** `true` exactly when `accrual_status` is -1, the engine's non-accrual code */
  non_accrual?: boolean;
  accrual_status?: number;
}

/** One `COAAccount`. `parent` is null on a root (the BRD's `-1`). */
export interface Irrm2CoaAccount {
  account_num: string;
  parent: string | null;
  name: string;
  account_type: string;
  /** ⚠ only `Fixed Income` and `Non-Maturity` are valued in EVE (Luke's fact 4) */
  classification: string;
  mnemonic?: string;
  /** the Partitions tab's `Accrual Mtd` (PROD-2216) — a position without its own day count takes it */
  day_count_convention?: Irrm2DayCountConvention;
}

export interface Irrm2Product {
  coa_account: string;
  assumption_set?: string;
}

/** `CurveSettings` — every member but the short end and the floor is required, with no default (D2). */
export interface Irrm2CurveSettings {
  accrual_method: Irrm2DayCountConvention;
  compounding: 'annual' | 'semiannual' | 'continuous';
  interpolation: 'Auto-Interp' | 'log_linear_df' | 'akima_spot' | 'linear_zero' | 'monotone_cubic';
  spot_interpolation: 'log_linear_df' | 'akima_spot' | 'linear_zero' | 'monotone_cubic';
  extrapolation: string;
  short_end_accrual_method?: Irrm2DayCountConvention;
  short_end_term_months?: number;
  /**
   * a decimal; `0` is the declarations' `Zero`, `null` is unfloored. Emport always sends it (PROD-2748):
   * from engine 0.5.0 an absent key means a 0.0 floor, not none.
   */
  rate_shock_floor?: number | null;
}

/** `RateIndex`. `linked_curve` names a curve OR another index, and must resolve in one of them. */
export interface Irrm2RateIndex {
  pricing_term_months: number;
  lag_type: 'month';
  lag_factor: number;
  linked_curve: string;
  rate_add: number;
  floor?: number;
}

export type Irrm2ScenarioType = 'parallel' | 'ramp' | 'non_parallel' | 'basis';

/** `Scenario`. A ramp may only apply to `nii` (D7). */
export interface Irrm2Scenario {
  scenario_id: string;
  scenario_type: Irrm2ScenarioType;
  applies_to: 'nii' | 'eve' | 'both';
  magnitude_bps?: number;
  ramp_length_periods?: number;
  /** term months → bps */
  shock_vector?: Record<string, number>;
  /** curve or index name → bps */
  curve_shocks?: Record<string, number>;
}

/**
 * One `AssumptionSet` (PROD-2216), keyed in `assumptions` by the COA account's MNEMONIC — the key the
 * engine's ladder lookup walks up the chart with, never `products[*].assumption_set`. Each ladder is a
 * JSON object of integer keys: shock BASIS POINTS for beta, lag and prepay (`"-400"` … `"400"`), MONTHS
 * from the as-of for decay. Values are decimal fractions, a lag is months. The engine reads beta, lag and
 * decay for `nmd` positions only, prepay for term records only; `new_volume`, `cap` and `floor` are its
 * NOT_IMPLEMENTED members, so Emport never sends them.
 */
export interface Irrm2AssumptionSet {
  beta_ladder?: Record<string, number>;
  lag_ladder?: Record<string, number>;
  /** CUMULATIVE run-off fraction by month, 0 to 1 — the engine reads it as steps, so it is monthly */
  decay_ladder?: Record<string, number>;
  /** CPR by shock — not zero at the base column */
  prepay_ladder?: Record<string, number>;
}

/**
 * `DiscountBasis` (`options.discount_basis_by_account`), keyed by COA `account_num`. Emport sends only
 * `flat_rate`: the account's base-case EVE discount rate, which the engine moves with the scenario's own
 * shift in the run discount curve.
 */
export interface Irrm2DiscountBasis {
  flat_rate: number;
}

/**
 * `ReinvestmentAssumption` (PROD-2220), keyed in `reinvestment` by COA `account_num`: what runs off the account
 * is placed again, for `term_months`, at `rate_ladder`'s rate for the run's shock. Ladders are JSON objects of
 * integer shock BASIS POINTS (`"-400"` … `"400"`); a rate is a decimal fraction, a lag is months. The engine
 * takes `rate_ladder` XOR `rate_curve`; Emport sends only the ladder. `rate_lag_months_ladder` delays the
 * shocked RATE (the volume is placed on time, at the base column's rate meanwhile). Emport never sends
 * `lag_months_ladder`, the engine's idle window, which is not what Empyrean's lag means.
 */
export interface Irrm2ReinvestmentAssumption {
  /** the share of the run-off placed again: Emport sends 1.0, as the file's BSTargets hold every account Flat */
  pct: number;
  /** 1 … 600 */
  term_months: number;
  rate_ladder: Record<string, number>;
  rate_lag_months_ladder?: Record<string, number>;
}

/** The engine's `EngineInput`, with `curves` left for the sender to fill from `par_quotes`. */
export interface Irrm2EngineInput {
  as_of_date: string;
  run_id?: string;
  instruments: Irrm2Instrument[];
  coa: Irrm2CoaAccount[];
  products: Record<string, Irrm2Product>;
  /** term months → ZERO rate; empty in the envelope, filled by the sender's bootstrap */
  curves: Record<string, Record<string, number>>;
  curve_settings: Record<string, Irrm2CurveSettings>;
  rate_indexes: Record<string, Irrm2RateIndex>;
  /** COA mnemonic → its ladders (PROD-2216) */
  assumptions: Record<string, Irrm2AssumptionSet>;
  scenarios: Irrm2Scenario[];
  /**
   * COA `account_num` → how its run-off is placed again (PROD-2220). An account left out runs off statically.
   * Absent from an envelope built before phase 6, which the engine reads as no account reinvested.
   */
  reinvestment?: Record<string, Irrm2ReinvestmentAssumption>;
}

/** `RunAnalysisOptionsRequest` — the members Emport sets; the rest stay the engine's defaults. */
export interface Irrm2AnalyzeOptions {
  horizon_months: number;
  /** `treasury` resolves to the curve named `TreasCV`, which the run must carry */
  discount_curve_choice: 'treasury' | 'swap_sofr';
  compute_sensitivities: boolean;
  /** whether a `call_schedule` / `put_schedule` is exercised; the engine's default is `none` */
  option_exercise?: 'none' | 'deterministic';
  /**
   * COA account number → its EVE discount rate (PROD-2216): the rate the Empyrean file discounts the same
   * account at. Absent on an account the engine discounts on `discount_curve_choice`.
   */
  discount_basis_by_account?: Record<string, Irrm2DiscountBasis>;
}

/** One par quote the sender bootstraps — the pack's own point, as a decimal. */
export interface Irrm2ParQuote {
  /** 0 is real: the OIS overnight point */
  term_months: number;
  rate: number;
  ticker: string | null;
  /** the pack series the quote came from, when it is not the curve's own name (`TOPEFHLBCV` for `FHLBCV`) */
  source_series?: string;
}

/** Something the envelope deliberately does not carry, and why — for the reader of the payload. */
export interface Irrm2Omission {
  section: string;
  reason: string;
}

export interface Irrm2SubmissionCounts {
  instruments: number;
  coa_accounts: number;
  scenarios: number;
  curves: number;
  /** UTF-8 bytes of `{ engine_input, options }` as sent, before the bootstrapped curves are added */
  bytes: number;
}

export interface Irrm2Submission {
  envelope: typeof IRRM2_SUBMISSION_ENVELOPE;
  portfolio_code: string;
  /** yyyy-MM-dd */
  as_of_date: string;
  built_by: string | null;
  counts: Irrm2SubmissionCounts;
  /** curve mnemonic → its par quotes, in term order */
  par_quotes: Record<string, Irrm2ParQuote[]>;
  engine_input: Irrm2EngineInput;
  options: Irrm2AnalyzeOptions;
  omitted: Irrm2Omission[];
}
